import * as React from 'react';

import LoadableERPBarChart from './LoadableERPBarChart';
import LoadableERPComparisonChart from './LoadableERPComparisonChart';
import ERPRevenueSummary from './ERPRevenueSummary';

import { ERPRequestApiResult } from './api/ERPSessionRequest';
import { ErpScenarioDetailResponse } from 'com.cogvio.pricemonitor.api';
import { ERPInterventionRequest, ERPScenarioError } from './api/ERPInterventionRequest';
import { ApiResult, useApiResult } from '~/infrastructure/mam/ApiHooks';
import { calculateModelAverage } from './ERPAveragePriceBadge';

import memoizeOne from 'memoize-one';
import { calculateModelRevenue } from './ERPRevenuePriceBadge';
import { formatNumberValueToShort } from './utils/number';
import ERPScenarioSelect, { BASE_SCENARIO_ID } from './ERPScenarioSelect';
import { useDispatch, useSelector } from 'react-redux';
import { setLeftSelectedScenarioId, setRightSelectedScenarioId } from '~/ERP/store/actions';
import { ApplicationState } from '~/Store';

const options = [
	{ id: null, name: 'Choose scenario' },
	{ id: BASE_SCENARIO_ID, name: 'Base scenario' },
];
const BAR_CHART_COLORS = ['rgba(38, 41, 85, 0.8)'];

export const pickScenario = memoizeOne(
	<T extends { id: string | null; name?: string }>(scenarioId: string | null | undefined, scenarios: T[]) =>
		scenarioId ? scenarios.find((candidate) => candidate.id === scenarioId) : null
);

const pickRequestResult = (
	scenarioId: string | null,
	erpSessionResult: ERPRequestApiResult,
	result: ApiResult<unknown, unknown>
) => (scenarioId === BASE_SCENARIO_ID ? erpSessionResult : result);

interface ERPScenarioComparisonProps {
	currency: string;
	erpSessionResult: ERPRequestApiResult;
	userId: string;
	onDataAddRequest?: () => void;
}

const ERPScenarioComparison = ({
	currency,
	erpSessionResult,
	userId,
	onDataAddRequest,
}: ERPScenarioComparisonProps) => {
	const dispatch = useDispatch();

	const apiScenarios = React.useMemo(
		() => erpSessionResult.value?.erpSessionDetail.erpSession.scenarios || [],
		[erpSessionResult]
	);

	const scenarios = React.useMemo(() => options.concat(apiScenarios), [apiScenarios]);

	const { leftSelectedScenarioId, rightSelectedScenarioId } = useSelector(
		(state: ApplicationState) => state.erpScenarioComparisonState
	);

	const erpSessionDetail = erpSessionResult.value?.erpSessionDetail;
	const [leftScenarioResult] = useApiResult<ERPInterventionRequest, ErpScenarioDetailResponse, ERPScenarioError>(
		erpSessionDetail && leftSelectedScenarioId !== BASE_SCENARIO_ID
			? new ERPInterventionRequest(
					userId,
					erpSessionDetail.erpPack.id,
					erpSessionDetail.erpSession.id,
					leftSelectedScenarioId
			  )
			: null,
		[userId, leftSelectedScenarioId, erpSessionDetail]
	);
	const actualLeftScenarioResultModel = React.useMemo(
		() =>
			leftSelectedScenarioId === BASE_SCENARIO_ID
				? erpSessionResult.value?.erpSessionDetail.erpModelResult
				: leftScenarioResult.value?.erpModelResult,
		[leftSelectedScenarioId, erpSessionResult, leftScenarioResult]
	);

	const [rightScenarioResult] = useApiResult<ERPInterventionRequest, ErpScenarioDetailResponse, ERPScenarioError>(
		erpSessionDetail && rightSelectedScenarioId && rightSelectedScenarioId !== BASE_SCENARIO_ID
			? new ERPInterventionRequest(
					userId,
					erpSessionDetail.erpPack.id,
					erpSessionDetail.erpSession.id,
					rightSelectedScenarioId
			  )
			: null,
		[userId, rightSelectedScenarioId, erpSessionDetail]
	);

	const actualRightScenarioResultModel = React.useMemo(
		() =>
			rightSelectedScenarioId === BASE_SCENARIO_ID
				? erpSessionResult.value?.erpSessionDetail.erpModelResult
				: rightScenarioResult.value?.erpModelResult,
		[rightSelectedScenarioId, erpSessionResult, rightScenarioResult]
	);

	return (
		<div className='px-10 bg-white'>
			<div className='pt-2 pb-20 w-full max-w-7xl mx-auto'>
				<h2 className='mb-1 font-display text-h2-head font-semibold text-cPurple-600'>Comparison</h2>
				<div className='relative py-4 mx-auto max-w-xl w-full flex justify-between'>
					<div className='absolute left-1/2 top-0 w-px h-full bg-gray-200'>
						<button
							className='absolute left-0 top-5 -ml-3 text-cPurple-600 text-opacity-60 transition hover:text-opacity-100'
							disabled={!rightSelectedScenarioId}
							title='Switch comparison'
							onClick={() => {
								const currentLeftSideId = leftSelectedScenarioId;
								const currentRightSideId = rightSelectedScenarioId;
								if (!currentRightSideId) {
									return;
								}

								dispatch(setLeftSelectedScenarioId(currentRightSideId));
								dispatch(setRightSelectedScenarioId(currentLeftSideId));
							}}
						>
							<svg
								className='w-6 h-6'
								fill='none'
								stroke='currentColor'
								strokeWidth='1.5'
								viewBox='0 0 24 24'
								xmlns='http://www.w3.org/2000/svg'
							>
								<path
									d='M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5'
									strokeLinecap='round'
									strokeLinejoin='round'
								></path>
							</svg>
						</button>
					</div>
					<div className='w-1/2 pr-4'>
						<div className='w-full pb-2'>
							<ERPScenarioSelect
								disabledScenarioId={rightSelectedScenarioId}
								scenarios={scenarios}
								selectedScenarioId={leftSelectedScenarioId}
								onScenarioSelect={setLeftSelectedScenarioId}
							/>
						</div>
						<div className='text-right'>
							<ERPRevenueSummary currency={currency} erpModelResult={actualLeftScenarioResultModel} type='small' />
						</div>
					</div>
					<div className='w-1/2 pl-4'>
						<div className='w-full pb-2'>
							<ERPScenarioSelect
								disabledScenarioId={leftSelectedScenarioId}
								scenarios={scenarios}
								selectedScenarioId={rightSelectedScenarioId}
								onScenarioSelect={setRightSelectedScenarioId}
							/>
						</div>
						{rightSelectedScenarioId && (
							<div className=''>
								<ERPRevenueSummary currency={currency} erpModelResult={actualRightScenarioResultModel} type='small' />
							</div>
						)}
					</div>
				</div>
				<div className='py-4 text-center'>
					<div className='mx-auto w-full max-w-xl'>
						{rightSelectedScenarioId && (
							<table cellPadding='0' cellSpacing='0' width='100%'>
								<thead>
									<tr className='font-display text-md border-t-2 border-b-2 border-gray-200 bg-gray-100 bg-opacity-60'>
										<th className='px-3 py-1.5 text-left'>Scenario:</th>
										<th className='px-3 py-1.5 text-right'>Revenue (4Y)</th>
										<th className='px-3 py-1.5 text-right'>AVG price (in 4Y)</th>
									</tr>
								</thead>
								<tbody>
									<tr className='border-b border-gray-200'>
										<th className='px-3 py-2 font-display text-label font-semibold text-gray-500 text-left'>
											{pickScenario(leftSelectedScenarioId, scenarios)?.name}:
										</th>
										<td className='px-3 py-2 font-roboto text-label font-semibold text-cPurple-600 text-right tabular-nums'>
											{formatNumberValueToShort(calculateModelRevenue(actualLeftScenarioResultModel))}&nbsp;{currency}
										</td>
										<td className='px-3 py-2 font-roboto text-label font-semibold text-cPurple-600 text-right tabular-nums'>
											{formatNumberValueToShort(calculateModelAverage(actualLeftScenarioResultModel))}&nbsp;{currency}
											&nbsp;/&nbsp;SKU
										</td>
									</tr>
									<tr className='border-b border-gray-200'>
										<th className='px-3 py-2 font-display text-label font-semibold text-gray-500 text-left'>
											{pickScenario(rightSelectedScenarioId, scenarios)?.name}:
										</th>
										<td className='px-3 py-2 font-roboto text-label font-semibold text-cPurple-600 text-right tabular-nums'>
											{formatNumberValueToShort(calculateModelRevenue(actualRightScenarioResultModel))}&nbsp;{currency}
										</td>
										<td className='px-3 py-2 font-roboto text-label font-semibold text-cPurple-600 text-right tabular-nums'>
											{formatNumberValueToShort(calculateModelAverage(actualRightScenarioResultModel))}&nbsp;{currency}
											&nbsp;/&nbsp;SKU
										</td>
									</tr>
								</tbody>
							</table>
						)}
					</div>
				</div>
				<div className='relative py-4 flex justify-between'>
					<div className='w-1/2 pr-4'>
						<div className='relative'>
							<h4 className='mb-1 font-display px-4 text-h4 font-semibold text-cPurple-600'>
								{pickScenario(leftSelectedScenarioId, scenarios)?.name}
							</h4>
							<LoadableERPBarChart
								apiResult={pickRequestResult(leftSelectedScenarioId, erpSessionResult, leftScenarioResult)}
								colors={BAR_CHART_COLORS}
								currency={currency}
								erpModelResult={actualLeftScenarioResultModel}
								onDataAddRequest={leftSelectedScenarioId === BASE_SCENARIO_ID ? onDataAddRequest : undefined}
							/>
						</div>
					</div>
					<div className='w-1/2 pl-4'>
						<div className='relative'>
							<h4 className='mb-1 font-display px-4 text-h4 font-semibold text-cPurple-600'>
								{pickScenario(rightSelectedScenarioId, scenarios)?.name || '\u00A0'}
							</h4>
							<LoadableERPComparisonChart
								apiResult={pickRequestResult(rightSelectedScenarioId, erpSessionResult, rightScenarioResult)}
								baseModelResult={erpSessionResult.value?.erpSessionDetail.erpModelResult}
								currency={currency}
								empty={!rightSelectedScenarioId}
								leftModelResult={actualLeftScenarioResultModel}
								rightModelResult={actualRightScenarioResultModel}
								scenarios={scenarios}
								onScenarioSelect={setRightSelectedScenarioId}
							/>
						</div>
					</div>
				</div>
				<div className='flex justify-between'>
					<div className='w-1/2 pr-4'>
						<div className='mb-2.5 flex items-center justify-start'>
							<i className='relative top-0 -mt-px block mr-1.5 w-2.5 h-2.5 rounded-sm bg-cPurple-800'></i>
							<p className='font-display text-labelsmall font-medium leading-none text-gray-600 tracking-wider'>
								first selected scenario (left): revenue after 4 years
							</p>
						</div>
					</div>
					<div className='w-1/2 pl-4'>
						<div className='mb-2.5 flex items-center justify-start'>
							<i className='relative top-0 -mt-px block mr-1.5 w-2.5 h-2.5 rounded-sm bg-cEmerald-500'></i>
							<p className='font-display text-labelsmall font-medium leading-none text-gray-600 tracking-wider'>
								second selected scenario (right): revenue after 4 years — higher than in first selected scenario
							</p>
						</div>
						<div className='mb-2.5 flex items-center justify-start'>
							<i className='relative top-0 -mt-px block mr-1.5 w-2.5 h-2.5 rounded-sm bg-red-700'></i>
							<p className='font-display text-labelsmall font-medium leading-none text-gray-600 tracking-wider'>
								second selected scenario (right): revenue after 4 years — lower than in first selected scenario
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default React.memo(ERPScenarioComparison);
